/**
 * Agrupa encaminhamentos em Família vs escola (nome do colégio).
 */

function normalizeKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function guardianNameKeys(ata) {
  const keys = new Set();
  (ata && ata.students ? ata.students : []).forEach(s => {
    (s.guardians || []).forEach(g => {
      const key = normalizeKey(g.name);
      if (key) keys.add(key);
    });
  });
  return keys;
}

function looksLikeFamilyAssignee(assigneeKey) {
  if (!assigneeKey) return false;
  if (/pedagog|financeir/.test(assigneeKey)) return false;
  return (
    /\bfamilia\b/.test(assigneeKey) ||
    /\bresponsave(l|is)\b/.test(assigneeKey) ||
    /\bmae\b/.test(assigneeKey) ||
    /\bpai\b/.test(assigneeKey) ||
    /\bav[oô]\b/.test(assigneeKey) ||
    /\btia\b|\btio\b/.test(assigneeKey)
  );
}

function looksLikeFamilyDescription(descKey) {
  if (!descKey) return false;
  return (
    /^a familia\b/.test(descKey) ||
    /^em casa\b/.test(descKey) ||
    /^os responsaveis\b/.test(descKey) ||
    /^a mae\b/.test(descKey) ||
    /^o pai\b/.test(descKey)
  );
}

function assigneeMatchesGuardian(assigneeKey, guardians) {
  if (!assigneeKey || !guardians.size) return false;
  return Array.from(guardians).some(g => {
    if (assigneeKey === g || assigneeKey.includes(g) || g.includes(assigneeKey)) {
      return true;
    }
    const at = assigneeKey.split(/\s+/);
    const bt = g.split(/\s+/);
    return (
      at.length >= 2 &&
      bt.length >= 2 &&
      at[0] === bt[0] &&
      at[at.length - 1] === bt[bt.length - 1]
    );
  });
}

/** true = responsabilidade da família; false = da escola. */
export function isFamilyAction(action, ata) {
  if (!action) return false;
  const audience = normalizeKey(action.audience || action.group);
  if (audience === "family" || audience === "familia") return true;
  if (audience === "school" || audience === "escola" || audience === "colegio") {
    return false;
  }

  const assigneeKey = normalizeKey(action.assignee);
  const descKey = normalizeKey(action.description);
  const guardians = guardianNameKeys(ata);

  if (assigneeMatchesGuardian(assigneeKey, guardians)) return true;
  if (looksLikeFamilyAssignee(assigneeKey)) return true;
  if (looksLikeFamilyDescription(descKey)) return true;
  return false;
}

/**
 * @returns {{ family: object[], school: object[] }}
 */
export function groupActionsByAudience(actions, ata) {
  const family = [];
  const school = [];
  (actions || []).forEach(action => {
    if (isFamilyAction(action, ata)) family.push(action);
    else school.push(action);
  });
  return { family, school };
}

const STOP_WORDS = new Set([
  "a",
  "o",
  "os",
  "as",
  "de",
  "da",
  "do",
  "das",
  "dos",
  "e",
  "em",
  "um",
  "uma",
  "para",
  "com",
  "que",
  "por",
  "na",
  "no",
  "nas",
  "nos",
  "se",
  "ao",
  "à",
  "sua",
  "seu",
  "suas",
  "seus"
]);

function significantTokens(text) {
  return normalizeKey(text)
    .split(/[^a-z0-9]+/)
    .filter(t => t.length >= 3 && !STOP_WORDS.has(t));
}

/** Sobreposição Jaccard simplificada entre tokens significativos. */
export function textOverlapScore(a, b) {
  const ta = new Set(significantTokens(a));
  const tb = new Set(significantTokens(b));
  if (!ta.size || !tb.size) return 0;
  let inter = 0;
  ta.forEach(t => {
    if (tb.has(t)) inter += 1;
  });
  const union = ta.size + tb.size - inter;
  return union ? inter / union : 0;
}

function isOpenAction(action) {
  const s = normalizeKey(action && action.status);
  return s !== "done" && s !== "cancelled" && s !== "concluido" && s !== "cancelado";
}

/**
 * Ao confirmar uma fala, sugere/atualiza responsável de encaminhamentos
 * abertos cujo texto combina com o conteúdo da fala.
 * Retorna patch { actions } ou null.
 */
export function applyAssigneeFromConfirmedTurn(ata, turn, person) {
  if (!ata || !turn || !person || !person.name) return null;
  const actions = ata.actions || [];
  if (!actions.length) return null;

  const turnBlob = [turn.body, turn.text, turn.name]
    .filter(Boolean)
    .join(" ");
  const personName = String(person.name).trim();
  const personKey = normalizeKey(personName);
  const personId = person.id || null;
  const isGuardian =
    String(personId || "").startsWith("guardian:") ||
    /mae|pai|responsavel|familia/i.test(String(person.role || ""));

  let changed = false;
  const next = actions.map(action => {
    if (!isOpenAction(action)) return action;
    const desc = action.description || "";
    const overlap = textOverlapScore(turnBlob, desc);
    const assigneeKey = normalizeKey(action.assignee);
    const alreadySame =
      assigneeKey === personKey ||
      (assigneeKey &&
        personKey &&
        (assigneeKey.includes(personKey) || personKey.includes(assigneeKey)));
    if (alreadySame) return action;

    const familyAction = isFamilyAction(action, ata);
    // Família genérica + responsável falando do tema
    const genericFamily =
      familyAction &&
      (!assigneeKey || looksLikeFamilyAssignee(assigneeKey));
    // Troca quando a fala e o encaminhamento falam da mesma coisa
    const contentMatch = overlap >= 0.22;
    // Guardião confirmado em encaminhamento de família com algum overlap
    const guardianFamily =
      isGuardian && familyAction && overlap >= 0.12;

    if (!(contentMatch || genericFamily || guardianFamily)) {
      return action;
    }
    // Evita trocar responsável escolar por guardião sem match forte
    if (
      !familyAction &&
      isGuardian &&
      overlap < 0.28 &&
      assigneeKey &&
      !looksLikeFamilyAssignee(assigneeKey)
    ) {
      return action;
    }

    changed = true;
    return {
      ...action,
      assignee: personName,
      assigneePersonId: personId,
      assigneeSource: "voice_confirm",
      audience: familyAction || isGuardian ? action.audience || "family" : action.audience
    };
  });

  return changed ? { actions: next } : null;
}
